import React, { useState } from 'react';
import { X, Building2, Phone, CheckCircle2, ArrowRight } from 'lucide-react';
import { usePopup } from '../context/PopupContext';

const LeadPopup: React.FC = () => {
    const { isPopupOpen, closePopup, projectName } = usePopup();
    const [formData, setFormData] = useState({
        name: '',
        phone: '',
        project: projectName || ''
    });
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isSuccess, setIsSuccess] = useState(false);

    // Update internal state if projectName changes from context
    React.useEffect(() => {
        if (projectName) {
            setFormData(prev => ({ ...prev, project: projectName }));
        }
    }, [projectName]);

    if (!isPopupOpen) return null;

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setIsSubmitting(true);

        // Validate
        if (!formData.name || !formData.phone) {
            alert('Please fill in all fields');
            setIsSubmitting(false);
            return;
        }

        try {
            const GOOGLE_SCRIPT_URL = 'https://script.google.com/macros/s/AKfycbxykQSU-HiL3vN4oEEXn8Satcyv96AGar6J6-PIb387PNkgFNHWkonQlT5wOnp596m6/exec';

            // Use 'no-cors' mode for Google Apps Script
            await fetch(GOOGLE_SCRIPT_URL, {
                method: 'POST',
                mode: 'no-cors',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    ...formData,
                    formType: 'popup_lead', // Identify source
                    timestamp: new Date().toISOString()
                })
            });

            // Since no-cors returns opaque response, we assume success if no error thrown
            setIsSuccess(true);
            setTimeout(() => {
                closePopup();
                setIsSuccess(false);
                setFormData({ name: '', phone: '', project: '' });
            }, 3000);

        } catch (error) {
            console.error('Submission error:', error);
            alert('Something went wrong. Please try again.');
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 animate-fade-in">
            {/* Backdrop */}
            <div
                className="absolute inset-0 bg-black/60 backdrop-blur-sm transition-opacity"
                onClick={closePopup}
            ></div>

            {/* Modal Content */}
            <div className="relative bg-white w-full max-w-md rounded-2xl shadow-2xl overflow-hidden transform transition-all scale-100 animate-slide-up">

                {/* Close Button */}
                <button
                    onClick={closePopup}
                    className="absolute top-4 right-4 z-10 p-2 bg-white/20 hover:bg-white/40 rounded-full text-gray-500 hover:text-gray-800 transition-colors"
                >
                    <X className="w-5 h-5" />
                </button>

                {/* Header with Image or Gradient */}
                <div className="h-32 bg-gray-900 relative overflow-hidden flex items-center justify-center text-center px-6">
                    <div className="absolute inset-0 bg-[url('https://images.unsplash.com/photo-1600607687939-ce8a6c25118c?q=80&w=2653&auto=format&fit=crop')] bg-cover bg-center opacity-40"></div>
                    <div className="absolute inset-0 bg-gradient-to-t from-black/90 to-transparent"></div>

                    <div className="relative z-10 w-full">
                        <h3 className="text-2xl font-bold text-white font-bricolage mb-1">
                            {projectName ? `Interest in ${projectName}` : 'Find Your Dream Home'}
                        </h3>
                        <p className="text-gray-300 text-sm">
                            {projectName ? 'Get exclusive details & pricing.' : 'Get expert advice & exclusive deals.'}
                        </p>
                    </div>
                </div>

                {/* Form Body */}
                <div className="p-8">
                    {isSuccess ? (
                        <div className="text-center py-8">
                            <div className="w-16 h-16 bg-green-100 text-green-600 rounded-full flex items-center justify-center mx-auto mb-4 animate-bounce">
                                <CheckCircle2 className="w-8 h-8" />
                            </div>
                            <h3 className="text-2xl font-bold text-gray-800 mb-2">Thank You!</h3>
                            <p className="text-gray-500">Our expert will call you shortly.</p>
                        </div>
                    ) : (
                        <form onSubmit={handleSubmit} className="space-y-5">

                            <div className="space-y-1">
                                <label className="text-sm font-medium text-gray-700">Full Name</label>
                                <input
                                    type="text"
                                    required
                                    placeholder="Name"
                                    className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-primary-500 focus:ring-2 focus:ring-primary-200 outline-none transition-all bg-gray-50 focus:bg-white"
                                    value={formData.name}
                                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                                />
                            </div>

                            <div className="space-y-1">
                                <label className="text-sm font-medium text-gray-700">Phone Number</label>
                                <div className="flex">
                                    <span className="inline-flex items-center px-4 py-3 rounded-l-xl border border-r-0 border-gray-200 bg-gray-100 text-gray-500 font-medium">
                                        +91
                                    </span>
                                    <input
                                        type="tel"
                                        required
                                        placeholder="98765 43210"
                                        className="w-full px-4 py-3 rounded-r-xl border border-gray-200 focus:border-primary-500 focus:ring-2 focus:ring-primary-200 outline-none transition-all bg-gray-50 focus:bg-white"
                                        value={formData.phone}
                                        onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                                    />
                                </div>
                            </div>

                            <button
                                type="submit"
                                disabled={isSubmitting}
                                className="w-full flex items-center justify-center gap-2 bg-gradient-to-r from-gray-900 to-black hover:from-primary-600 hover:to-primary-700 text-white font-bold py-4 rounded-xl shadow-lg transform active:scale-95 transition-all duration-300 disabled:opacity-70 disabled:cursor-not-allowed"
                            >
                                {isSubmitting ? (
                                    <span className="animate-pulse">Sending...</span>
                                ) : (
                                    <>
                                        <span>Request Call Back</span>
                                        <ArrowRight className="w-5 h-5" />
                                    </>
                                )}
                            </button>

                            <p className="text-xs text-center text-gray-400 mt-4">
                                By submitting, you agree to our <span className="underline cursor-pointer">Terms</span> & <span className="underline cursor-pointer">Privacy Policy</span>.
                            </p>
                        </form>
                    )}
                </div>
            </div>
        </div>
    );
};

export default LeadPopup;
